import { useState, useEffect, RefObject, useCallback } from 'react';

export interface UseTextClampProps {
  ref: RefObject<HTMLElement | null>;
  content?: string | null;
}

/**
 * Hook to detect if an element's text content is visually clamped (truncated).
 *
 * @param ref - Ref to the DOM element to check.
 * @param content - Optional content to watch for rechecking.
 */
export function useTextClamp({ ref, content = null }: UseTextClampProps) {
  const [isClamped, setIsClamped] = useState(false);

  // Check if the element is clamped by comparing scrollHeight and clientHeight
  const checkClamping = useCallback(() => {
    const element = ref.current;

    if (!element) return;

    setIsClamped(element.scrollHeight > element.clientHeight);
  }, [ref]);

  // Recheck when content changes
  useEffect(() => {
    const id = requestAnimationFrame(checkClamping);
    return () => cancelAnimationFrame(id);
  }, [content, checkClamping]);

  // Recheck on window resize
  useEffect(() => {
    window.addEventListener('resize', checkClamping);
    return () => window.removeEventListener('resize', checkClamping);
  }, [checkClamping]);

  return { isClamped, recheck: checkClamping };
}
